import axios from 'axios';
import { handleRequest } from 'helpers/api';

const ORDERS_URL = `${process.env.REACT_APP_API_URL}/orders`;

const ordersApi = axios.create({
  baseURL: ORDERS_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

interface CreateOrderData {
  vendorId: string;
  paymentMethod: string;
  paymentTerm: string;
}

export interface Order {
  _id: string;
  orderId: string;
  vendorId: string;
  products: {
    productId: string;
    quantity: number;
    price: number;
  }[];
  status: string;
  totalAmount: number;
  shippingCost: number;
  paymentMethod: string;
  taxAmount: number;
  discountAmount: number;
  finalAmount: number;
  deliveryStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerProducts {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  thumbnail: string;
  vendorId: string;
  orderDate: Date;
  orderId: string;
}

interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
}

export interface OrderDetails {
  products: {
    productId: {
      _id: string;
      thumbnail: string;
      name: string;
    };
    quantity: number;
    price: number;
  }[];
  _id: string;
  orderId: string;
  vendorId: string;
  status: string;
  totalAmount: number;
  shippingCost: number;
  paymentMethod: string;
  taxAmount: number;
  discountAmount: number;
  finalAmount: number;
  deliveryStatus: string;
  createdAt: string;
  updatedAt: string;
  billingAddress: Address;
  shippingAddress: Address;
  paymentStatus?: string;
  fulfillmentStatus?: string;
}

export interface Tracking {
  status: string;
  date: string;
}

export interface OrderTracking {
  _id: string;
  orderId: string;
  orderTracking: Tracking[];
}

export interface VendorOrderTracking extends OrderTracking {
  customerName: string;
  products: number;
  shippingAddress: Address;
}

export const createOrder = async (data: CreateOrderData) => {
  return handleRequest(ordersApi.post('/create', data));
};

export const getOrdersByCustomer = async () => {
  return handleRequest(ordersApi.get<Order[]>('/customer'));
};

export const getProductsByCustomer = async () => {
  return handleRequest(ordersApi.get<CustomerProducts[]>('/customer/products'));
};

export const getOrderByVendor = async () => {
  return handleRequest(ordersApi.get<Order[]>('/vendor'));
};

export const updatePaymentStatus = async (
  orderId: string,
  paymentStatus: string
) => {
  return handleRequest(
    ordersApi.put(`/vendor/payment-status/${orderId}`, { paymentStatus })
  );
};

export const updateFulfillmentStatus = async (
  orderId: string,
  fulfillmentStatus: string
) => {
  return handleRequest(
    ordersApi.put(`/vendor/fulfillment-status/${orderId}`, {
      fulfillmentStatus
    })
  );
};

export const getOrderById = async (orderId: string) => {
  return handleRequest(
    ordersApi.get<OrderDetails>(`/order-details/${orderId}`)
  );
};

export const getOrderTracking = async () => {
  return handleRequest(
    ordersApi.get<OrderTracking[]>(`/customer/order-tracking`)
  );
};

export const getVendorOrderTracking = async () => {
  return handleRequest(
    ordersApi.get<VendorOrderTracking[]>(`/vendor/order-tracking`)
  );
};

export const updateOrderTracking = async (orderId: string, status: string) => {
  return handleRequest(
    ordersApi.put(`/vendor/order-tracking/${orderId}`, { status })
  );
};
